import * as React from 'react'
import { connect } from 'react-redux'
import { FeedbackForm } from './feedbackform'
import { ChatButton } from './chatbutton'
import { userIdFromAnswersHubSelector, hubSelector } from '../../selectors/hub'
import { currentAnswersArticleFromParamsDetailsSelector } from '../../selectors/articleDetails'
import { HubItem } from '../../reducers/hub'
import { scrollTo } from '../../lib/utils'

import '../../sass/answers/feedback.scss'
import { RootState } from '../../types/rootstate'
import { strings } from '../../lib/localization'
import { currentFeedbackVoteFromParamsDetailsSelector } from '../../selectors/feedback-votes'

interface StateProps {
  userId: number
  article: RootState['articleDetails']['key']['article']
  hub: HubItem[]
  feedbackVoteState: RootState['feedbackVotes']['key']
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface DispatchProps {}

interface OwnProps {
  scrollContainer: React.RefObject<HTMLDivElement>
  feedbackVote: (option: 'yes' | 'no') => void
}

type FeedbackProps = StateProps & DispatchProps & OwnProps

function mapStateToProps(state: RootState): StateProps {
  return {
    userId: userIdFromAnswersHubSelector(state),
    article: currentAnswersArticleFromParamsDetailsSelector(
      state
    ) as RootState['articleDetails']['key']['article'],
    hub: hubSelector(state),
    feedbackVoteState: currentFeedbackVoteFromParamsDetailsSelector(state)
  }
}

function mapDispatchToProps(): DispatchProps {
  return {}
}

interface FeedbackState {
  showEmailForm: boolean
  votedYes: boolean
  votedNo: boolean
  showThankYou: boolean
  thankYouText: string
  showChatAlternative: boolean
  showPhoneAlternative: boolean
  showEmailAlternative: boolean
  hasEmailForm: boolean
}

const FeedbackFormAlternative = (props: {
  subject: string
  body: string
  onClick?: () => void
}) => {
  const handleOnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    props.onClick && props.onClick()
  }

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      props.onClick && props.onClick()
    }
  }

  return (
    <div className='col-xs-12'>
      <div
        className='chatbutton accentSecondaryButton'
        onClick={handleOnClick}
        role='button'
        tabIndex={0}
        onKeyDown={handleOnKeyDown}
      >
        <div dangerouslySetInnerHTML={{ __html: props.subject }} />
        <div dangerouslySetInnerHTML={{ __html: props.body }} />
      </div>
    </div>
  )
}

class FeedbackComponent extends React.Component<FeedbackProps, FeedbackState> {
  private voteFeedbackContainer = React.createRef<HTMLDivElement>()

  constructor(props) {
    super(props)

    this.state = {
      showEmailForm: false,
      votedYes: false,
      votedNo: false,
      showThankYou: false,
      thankYouText: strings.answersVoteThankYou,
      showChatAlternative: false,
      showPhoneAlternative: false,
      showEmailAlternative: false,
      hasEmailForm: false
    }
  }

  componentDidMount() {
    const showChatAlternative =
      !!this.props.article.wasHelpfulChatForm &&
      this.props.hub &&
      this.props.hub.some((hubItem) => hubItem.type === 'online')
    const showPhoneAlternative = !!this.props.article.wasHelpfulPhoneForm

    const hasEmailForm = !!this.props.article.contactEmailId
    let showEmailForm = false
    let showEmailAlternative = !!this.props.article.wasHelpfulEmailForm
    if (
      !showChatAlternative &&
      !showPhoneAlternative &&
      !showEmailForm &&
      hasEmailForm
    ) {
      showEmailForm = true
      showEmailAlternative = false
    }

    let newState: Partial<FeedbackState> = {
      showChatAlternative,
      showPhoneAlternative,
      showEmailAlternative,
      hasEmailForm,
      showEmailForm
    }

    if (this.props.feedbackVoteState?.vote) {
      if (this.props.feedbackVoteState.vote === 'no') {
        const hasAdditionalFeedback =
          showChatAlternative ||
          showPhoneAlternative ||
          showEmailAlternative ||
          hasEmailForm

        newState = {
          ...newState,
          votedYes: false,
          votedNo: true,
          showThankYou: !hasAdditionalFeedback
        }
      } else {
        newState = {
          ...newState,
          votedYes: true,
          votedNo: false,
          showThankYou: true
        }
      }
    }

    this.setState(newState)
  }

  handleEmailFormClick = () => {
    this.setState({
      showEmailForm: true
    })
  }

  doVoteYes = () => {
    this.props.feedbackVote('yes')
    this.setState({
      votedYes: true,
      votedNo: false,
      showThankYou: true
    })
  }

  handleVoteYesClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!this.state.votedYes && !this.state.votedNo) {
      this.doVoteYes()
    }
  }

  handleVoteYesKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ' && !this.state.votedYes && !this.state.votedNo) {
      e.preventDefault()
      this.doVoteYes()
    }
  }

  doVoteNo = () => {
    const hasAdditionalFeedback =
      this.state.showChatAlternative ||
      this.state.showPhoneAlternative ||
      this.state.showEmailAlternative ||
      this.state.hasEmailForm

    this.props.feedbackVote('no')
    this.setState(
      {
        votedNo: true,
        votedYes: false,
        showThankYou: !hasAdditionalFeedback
      },
      () => {
        if (hasAdditionalFeedback) {
          window.setTimeout(() => {
            if (this.props.scrollContainer && this.voteFeedbackContainer) {
              const containerOffsetTop = this.voteFeedbackContainer.current
                .offsetTop

              scrollTo(
                this.props.scrollContainer.current,
                containerOffsetTop,
                200
              )
            }
          })
        }
      }
    )
  }

  handleVoteNoClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!this.state.votedYes && !this.state.votedNo) {
      this.doVoteNo()
    }
  }

  handleVoteNoKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ' && !this.state.votedYes && !this.state.votedNo) {
      e.preventDefault()
      this.doVoteNo()
    }
  }

  render() {
    const hasAdditionalFeedback =
      this.state.showChatAlternative ||
      this.state.showPhoneAlternative ||
      this.state.showEmailAlternative ||
      this.state.hasEmailForm

    return (
      <>
        <div className='voteForm' ref={this.voteFeedbackContainer}>
          {!this.state.showThankYou && (
            <>
              <div className='voteQuestion'>{this.props.article.helpText}</div>

              <div className='voteOptions'>
                <div className='voteOption voteUp'>
                  <div
                    className={
                      'button btn-outline-primary accentSecondaryButton ' +
                      (this.state.votedYes ? 'active' : '')
                    }
                    onClick={this.handleVoteYesClick}
                    role='button'
                    tabIndex={0}
                    onKeyDown={this.handleVoteYesKeyDown}
                  >
                    <span>{this.props.article.helpYes}</span>
                  </div>
                </div>

                <div
                  className='voteOption voteDown'
                  onClick={this.handleVoteNoClick}
                  tabIndex={0}
                  role='button'
                  onKeyDown={this.handleVoteNoKeyDown}
                >
                  <div
                    className={
                      'button btn-outline-primary accentSecondaryButton ' +
                      (this.state.votedNo ? 'active' : '')
                    }
                  >
                    <span>{this.props.article.helpNo}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {this.state.votedNo &&
            !this.state.showThankYou &&
            hasAdditionalFeedback && (
              <div className='feedbackAlternatives'>
                {this.state.showChatAlternative && (
                  <ChatButton text={this.props.article.contactChatSubject} />
                )}

                {this.state.showPhoneAlternative && (
                  <FeedbackFormAlternative
                    subject={this.props.article.contactPhoneSubject}
                    body={this.props.article.contactPhoneBody}
                  />
                )}

                {this.state.showEmailAlternative &&
                  !this.state.showEmailForm && (
                    <FeedbackFormAlternative
                      subject={this.props.article.contactEmailSubject}
                      body={this.props.article.contactEmailBody}
                      onClick={this.handleEmailFormClick}
                    />
                  )}
                {this.state.hasEmailForm && this.state.showEmailForm && (
                  <FeedbackForm
                    onSubmit={(thankYouText?: string) =>
                      this.setState({
                        showThankYou: true,
                        thankYouText:
                          thankYouText || strings.answersVoteThankYou
                      })
                    }
                    userId={this.props.userId}
                    formId={this.props.article.contactEmailId}
                    articleId={this.props.article.id}
                  />
                )}
              </div>
            )}

          {this.state.showThankYou && (
            <div className='voteThankYou'>{this.state.thankYouText}</div>
          )}
        </div>
      </>
    )
  }
}

const Feedback = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(FeedbackComponent)

export { Feedback }
